/**
 * @fileoverview Componente principal de la página de inicio - Velas Artesanales Argentina
 * @author Jonatan Bianchi - Bianchi Desarrollo Web
 * @website https://jonatan.bianchi.com.ar
 * @email jonatan@bianchi.com.ar
 * @company Bianchi Desarrollo Web
 * @website https://www.bianchi.com.ar
 * @email info@bianchi.com.ar
 * @copyright 2025 Bianchi Desarrollo Web
 */

import { Component, signal, inject, PLATFORM_ID, AfterViewInit, OnInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HeaderComponent } from '../components/header.component';
import { FooterComponent } from '../components/footer.component';
import { ContactoService } from '../services/contacto.service';
import { ProductosService } from '../services/productos.service';
import { Linea } from '../models/catalog.model';
import { environment } from '../../environments/environment';
import { take } from 'rxjs';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface RecaptchaEnterprise {
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
  ready: (callback: () => void) => void;
}

/**
 * Componente de la página de inicio
 * Maneja el formulario de contacto con integración de reCAPTCHA Enterprise
 */
@Component({
  selector: 'app-home',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, HeaderComponent, FooterComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements AfterViewInit, OnInit {
  protected readonly currentYear = new Date().getFullYear();
  protected readonly contactoForm: FormGroup;
  protected readonly isSubmitting = signal(false);
  protected readonly submitSuccess = signal(false);
  protected readonly submitError = signal<string | null>(null);
  protected readonly recaptchaReady = signal(false);
  protected highlightLineas: Linea[] = [];
  
  private platformId = inject(PLATFORM_ID);
  protected readonly RECAPTCHA_SITE_KEY = environment.recaptcha.siteKey;

  constructor(
    private fb: FormBuilder,
    private contactoService: ContactoService,
    private productosService: ProductosService
  ) {
    this.contactoForm = this.fb.group({
      nombre: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      asunto: [''],
      mensaje: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  /**
   * Inicializa el componente y carga las líneas
   */
  ngOnInit(): void {
    this.productosService.getProductos$()
      .pipe(take(1))
      .subscribe(() => {
        this.highlightLineas = this.productosService.getLineas().slice(0, 6);
      });
  }

  /**
   * Inicializa el componente y carga reCAPTCHA
   */
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadRecaptchaScript();
      setTimeout(() => {
        this.checkRecaptcha();
      }, 100);
    }
  }

  /**
   * Carga dinámicamente el script de reCAPTCHA Enterprise
   */
  private loadRecaptchaScript(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const existingScript = document.getElementById('recaptcha-script');
    if (existingScript && existingScript.getAttribute('src')) {
      return;
    }

    const script = existingScript || document.createElement('script');
    script.id = 'recaptcha-script';
    script.setAttribute('src', `https://www.google.com/recaptcha/enterprise.js?render=${this.RECAPTCHA_SITE_KEY}`);
    script.setAttribute('async', 'true');
    script.setAttribute('defer', 'true');
    
    if (!existingScript) {
      document.body.appendChild(script);
    }
  }

  /**
   * Verifica que reCAPTCHA esté cargado y disponible
   */
  private checkRecaptcha(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (typeof window.grecaptcha === 'undefined') {
      const maxAttempts = 20;
      let attempts = 0;
      
      const checkInterval = setInterval(() => {
        attempts++;
        const grecaptcha = window.grecaptcha;
        const hasExecute = grecaptcha && (grecaptcha.execute || (grecaptcha.enterprise && grecaptcha.enterprise.execute));
        
        if (hasExecute) {
          this.recaptchaReady.set(true);
          clearInterval(checkInterval);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
        }
      }, 500);
    } else {
      const grecaptcha = window.grecaptcha;
      const hasExecute = grecaptcha && (grecaptcha.execute || (grecaptcha.enterprise && grecaptcha.enterprise.execute));
      
      if (hasExecute) {
        this.recaptchaReady.set(true);
      } else {
        setTimeout(() => this.checkRecaptcha(), 500);
      }
    }
  }

  /**
   * Maneja el envío del formulario
   * Valida los datos y ejecuta reCAPTCHA antes de enviar
   */
  protected handleSubmit(): void {
    if (this.contactoForm.invalid) {
      Object.keys(this.contactoForm.controls).forEach(key => {
        this.contactoForm.get(key)?.markAsTouched();
      });
      this.submitError.set('Por favor, completa todos los campos requeridos.');
      return;
    }

    this.ejecutarRecaptcha();
  }

  /**
   * Ejecuta reCAPTCHA Enterprise para obtener un token de verificación
   */
  private ejecutarRecaptcha(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.submitError.set('reCAPTCHA no está disponible. Por favor, recarga la página.');
      return;
    }

    if (typeof window.grecaptcha === 'undefined') {
      this.submitError.set('reCAPTCHA no está disponible. Por favor, recarga la página.');
      return;
    }

    const grecaptcha = window.grecaptcha;
    const executeMethod = grecaptcha.execute || (grecaptcha.enterprise && grecaptcha.enterprise.execute);
    const readyMethod = grecaptcha.ready || (grecaptcha.enterprise && grecaptcha.enterprise.ready);

    if (!executeMethod) {
      this.submitError.set('reCAPTCHA no está disponible. Por favor, recarga la página.');
      return;
    }

    this.isSubmitting.set(true);
    this.submitError.set(null);
    this.submitSuccess.set(false);

    try {
      const executeRecaptchaCall = () => {
        executeMethod(this.RECAPTCHA_SITE_KEY, { action: 'submit' })
          .then((token: string) => {
            this.procesarFormularioConToken(token);
          })
          .catch((error: any) => {
            this.submitError.set('Error al verificar reCAPTCHA. Por favor, intenta nuevamente.');
            this.isSubmitting.set(false);
          });
      };

      if (readyMethod) {
        readyMethod(() => {
          executeRecaptchaCall();
        });
      } else {
        setTimeout(() => {
          executeRecaptchaCall();
        }, 500);
      }
    } catch (error) {
      this.submitError.set('Error al verificar reCAPTCHA. Por favor, intenta nuevamente.');
      this.isSubmitting.set(false);
    }
  }

  /**
   * Procesa el formulario con el token de reCAPTCHA obtenido
   * @param token Token de verificación de reCAPTCHA
   */
  private procesarFormularioConToken(token: string): void {
    if (!token) {
      this.submitError.set('Error al verificar reCAPTCHA. Por favor, intenta nuevamente.');
      this.isSubmitting.set(false);
      return;
    }

    this.enviarFormulario(token);
  }

  /**
   * Envía el formulario de contacto a Firestore
   * @param recaptchaToken Token de verificación de reCAPTCHA
   */
  private enviarFormulario(recaptchaToken: string): void {
    const formData = {
      ...this.contactoForm.value,
      recaptchaToken
    };

    this.contactoService.guardarContacto(formData).subscribe({
      next: () => {
        this.submitSuccess.set(true);
        this.contactoForm.reset();
        setTimeout(() => {
          this.submitSuccess.set(false);
        }, 5000);
        this.isSubmitting.set(false);
      },
      error: (error) => {
        this.submitError.set('Hubo un error al enviar tu mensaje. Por favor, intenta nuevamente.');
        this.isSubmitting.set(false);
      }
    });
  }

  /**
   * Obtiene el mensaje de error para un campo del formulario
   * @param fieldName Nombre del campo
   * @returns Mensaje de error o cadena vacía si no hay error
   */
  protected getFieldError(fieldName: string): string {
    const field = this.contactoForm.get(fieldName);
    if (field?.hasError('required') && field?.touched) {
      return `${this.getFieldLabel(fieldName)} es requerido`;
    }
    if (field?.hasError('email') && field?.touched) {
      return 'Por favor, ingresa un email válido';
    }
    if (field?.hasError('minlength') && field?.touched) {
      const minLength = field.errors?.['minlength']?.requiredLength;
      return `${this.getFieldLabel(fieldName)} debe tener al menos ${minLength} caracteres`;
    }
    return '';
  }

  /**
   * Obtiene la etiqueta traducida de un campo
   * @param fieldName Nombre del campo
   * @returns Etiqueta traducida
   */
  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      nombre: 'El nombre',
      email: 'El email',
      mensaje: 'El mensaje'
    };
    return labels[fieldName] || fieldName;
  }

  /**
   * Remueve el prefijo "Linea " del nombre de la línea
   */
  protected getNombreSinLinea(nombre: string): string {
    return nombre.replace(/^Linea\s+/i, '');
  }
}
